/**
 * @file usePickupPoint.ts
 * @description Подтягивает адрес и график ПВЗ по его коду (phase19.12 шаг 5).
 *
 * В заказе и в отгрузке хранится только код пункта — менеджеру этого мало,
 * ему нужен адрес и часы работы. Берём из кэша ПВЗ магазина; не нашли —
 * показываем один код, экран из-за этого не ломается.
 */

import { useEffect, useState } from "react";

import { listSdekPickupPoints, type PickupPointDto } from "../../lib/api-carriers-sdek";

interface Result {
  point: PickupPointDto | null;
  loading: boolean;
}

export function usePickupPoint(
  code: string | null | undefined,
  city?: string | null,
): Result {
  const [point, setPoint] = useState<PickupPointDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const wanted = code?.trim();
    if (!wanted) {
      setPoint(null);
      return;
    }
    let alive = true;
    setLoading(true);
    // Город приходит из поля ввода — без задержки был бы запрос на каждый символ.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const list = await listSdekPickupPoints({ city: city?.trim() || undefined });
          if (!alive) return;
          setPoint(list.find((p) => p.externalCode === wanted) ?? null);
        } catch {
          // Справка не критична: молча остаёмся с кодом пункта.
          if (alive) setPoint(null);
        } finally {
          if (alive) setLoading(false);
        }
      })();
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [code, city]);

  return { point, loading };
}
